import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { WORKSPACE_PATH, resolveAppFromPath } from '@/lib/apps';
import type { AppKey } from '@/lib/apps';

export interface ActiveApp {
  /** The app the user is in, from the browser URL. `null` on Home / platform
   *  pages, and `null` in the workspace — there the workspace itself is what is
   *  active, not any single app. */
  activeApp: AppKey | null;
  isWorkspace: boolean;
  /** The app the user is effectively working in: inside the workspace this
   *  becomes the focused pane's app (see WorkspaceContext), elsewhere it
   *  equals `activeApp`. */
  focusedApp: AppKey | null;
  /** What the app launcher should highlight. Same as `activeApp`, except in
   *  the workspace, where the Workspace tile itself is what is active. */
  launcherKey: AppKey | null;
}

/**
 * One shared answer to "which app am I in".
 * See docs/MULTI_APP_WORKSPACE.md §6.
 *
 * Note for Phase 2: this reads the router location, so it must only be called
 * OUTSIDE a workspace pane (inside a pane, `useLocation` is the pane's own
 * location). Pane-local code asks its pane instead.
 */
export function useActiveApp(): ActiveApp {
  const { pathname } = useLocation();

  return useMemo(() => {
    const isWorkspace = pathname === WORKSPACE_PATH || pathname.startsWith(`${WORKSPACE_PATH}/`);
    const activeApp = isWorkspace ? null : resolveAppFromPath(pathname);
    return {
      activeApp,
      isWorkspace,
      focusedApp: activeApp,
      launcherKey: isWorkspace ? 'workspace' : activeApp,
    };
  }, [pathname]);
}
